import React, { useState, useCallback, useMemo } from 'react';
import { DEFAULT_THEME_COLOR } from 'appenv';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Slide } from '@material-ui/core';
import confirmationEmailResend from 'network/confirmationEmailResend';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import TextInput from 'components/input/TextInput';
import { useTranslation, Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import ModalButton from 'components/ModalButton';
import ModalContainer from 'components/landing/ModalContainer';
import RegisterAction from 'components/landing/RegisterActionButton';
import LandingModalTitle from 'components/landing/LandingModalTitle';
import EmailSentMessageView from 'components/landing/EmailSentMessageView';
import LandingModalHelperText from 'components/landing/LandingModalHelperText';
import LandingModalCloseIcon from 'components/landing/LandingModalCloseIcon';
import LandingModalPageSplitter from 'components/landing/LandingModalPageSplitter';
import LandingModalReminderText from 'components/landing/LandingModalReminderText';
import LandingModalDialog from 'components/landing/LandingModalDialog';
import ReminderNoticeText from 'components/landing/ReminderNoticeText';
import ContactSupport from './ContactSupport';

const ResendButton = styled(ModalButton)`
  font-size: 18px;
  line-height: 18px;
  margin-top: 24px;
`;

const EmailInput = styled(TextInput)`
  padding: 0;
  margin: 11px 0;

  input {
    font-size: 16px;
  }
`;

const ModalTransition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} mountOnEnter unmountOnExit {...props} />
));

const validateEmail = (email) => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

const ResetEmailModal = ({
  open,
  handleClose,
  hasRegisterEnded,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isResent, setIsResent] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [messageOfResent, setMessageOfResent] = useState('');
  const isMobile = useMediaQuery('(max-width: 960px)');
  const isEmailValid = useMemo(() => (email && email.trim() !== '' && validateEmail(email)), [email]);
  const { themeColor } = useSelector((s) => s.eventConfigs);
  const setResentFalse = useCallback(() => setIsResent(false), [setIsResent]);

  const onEmailChange = useCallback(({ target: { value } }) => {
    setEmail(value);
  }, [setEmail]);

  const onModalExited = () => {
    setEmail('');
    setIsResent(false);
    setIsResending(false);
    setMessageOfResent(false);
  };

  const onResendClick = useCallback(async () => {
    setIsResending(true);
    const resendResult = await confirmationEmailResend(email.trim());
    if (resendResult.success) {
      setIsResent(true);
      setMessageOfResent('');
    } else {
      setMessageOfResent('Our server is currently busy, please try again later.');
    }
    setIsResending(false);
  }, [setIsResending, setIsResent, setMessageOfResent, email]);

  const onEmailInputKeyPress = useCallback((event) => {
    if (!isEmailValid) return;
    if (event.keyCode === 13) {
      onResendClick();
    }
  }, [onResendClick, isEmailValid]);

  return (
    <LandingModalDialog
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      onExited={onModalExited}
      closeAfterTransition
      BackdropProps={{
        timeout: 500,
      }}
      TransitionComponent={ModalTransition}
      fullScreen={isMobile}
    >
      <ModalContainer>
        <LandingModalCloseIcon onClick={handleClose} />
        <LandingModalPageSplitter>
          {!isResent && (
            <div>
              <LandingModalTitle id="transition-modal-title">
                {t('lobby.registration.title', 'Send me the access link')}
              </LandingModalTitle>
              <LandingModalReminderText>
                <Trans
                  i18nKey="lobby.registration.message"
                  components={{ b: <ReminderNoticeText /> }}
                  defaults="Enter your <b>registered<\/b> email. Then click the access link in your mailbox to log in seamlessly."
                />
              </LandingModalReminderText>
              <EmailInput
                autoFocus
                readOnly={isResending}
                defaultValue={email}
                label={t('lobby.registration.registered_email', 'Your registered email')}
                placeholder={t('lobby.registration.enter_email', 'Enter email')}
                sublabel={t('lobby.registration.registered_message', '*Only registered & approved users can receive the access link.*')}
                helperColor="#E2654D"
                onChange={onEmailChange}
                onKeyPress={onEmailInputKeyPress}
              />
              {messageOfResent && (
                <LandingModalHelperText>
                  {messageOfResent}
                </LandingModalHelperText>
              )}
              <ResendButton
                onClick={onResendClick}
                disabled={isResending || !isEmailValid}
                color={themeColor || DEFAULT_THEME_COLOR}
              >
                {
                  isResending ? (
                    t('lobby.registration.sending', 'Sending')
                  ) : (
                    t('lobby.registration.send', 'Send')
                  )
                }
              </ResendButton>
              <ContactSupport handleClose={handleClose} />
            </div>
          )}
          {isResent && (<EmailSentMessageView email={email} showEmailInput={setResentFalse} handleClose={handleClose} />)}
          {(!isResent && !hasRegisterEnded) && (
            <RegisterAction />
          )}
        </LandingModalPageSplitter>
      </ModalContainer>
    </LandingModalDialog>
  );
};

ResetEmailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  hasRegisterEnded: PropTypes.bool.isRequired,
};

export default ResetEmailModal;
